import { isDate as isDateFn, isValid, parse, toDate as toDateFn } from 'date-fns';
import type { DateInput } from './types.ts';

export const isDate = (val: unknown): val is Date => isDateFn(val);

export const isValidDate = (val: unknown): val is Date => isDateFn(val) && isValid(val);

export function toDate(date: DateInput): Date | null {
  if (!date) return null;
  const d = isDate(date) || typeof date === 'number' ? toDateFn(date) : new Date(date);
  return isValid(d) ? d : null;
}

interface ParseOptions {
  /** Formats to try  */
  formats?: string[];
  /** If true, will try to use Date constructor to parse the date */
  fallbackToDate?: boolean;
}

/**
 * Date-only formats that should be parsed as UTC noon to avoid timezone issues.
 * Using noon (12:00) ensures the date is correct in all timezones from UTC-12 to UTC+12.
 */
const dateOnlyFormats = ['yyyyMMdd', 'yyyy-MM-dd', 'dd.MM.yyyy'];

const dateTimeFormats = ['yyyy-MM-dd HH:mm:ss'];

const wellKnownFormats = [...dateOnlyFormats, ...dateTimeFormats];

/**
 * Convert a date parsed in local timezone to UTC noon of the same calendar day.
 * This ensures date-only values display correctly regardless of timezone.
 */
const toUtcNoon = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0));
};

export const parseDate = (
  value: string | number | Date | null,
  options: ParseOptions = { fallbackToDate: true, formats: wellKnownFormats }
) => {
  const ref = new Date();
  const { formats = wellKnownFormats, fallbackToDate = true } = options;
  if (value === null) return null;
  if (typeof value === 'undefined') return new Date();
  if (typeof value === 'number' || isValidDate(value)) return toDate(value);

  for (const format of formats) {
    const val = parse(value, format, ref);
    if (isValidDate(val)) {
      // For date-only formats, use UTC noon to avoid timezone issues
      return dateOnlyFormats.includes(format) ? toUtcNoon(val) : val;
    }
  }

  if (fallbackToDate) {
    const date = new Date(value);
    if (isValidDate(date)) return date;
  }

  return null;
};
